// „История на промените" в документния модал (доставка, изписване/бракуване,
// инвентаризация, прехвърляне) — над листа на документа, никога в печата.
//
// Без редакции → един приглушен ред „Без промени след създаването". С редакции
// → сгъната лента „История на промените · 2 редакции · последна 28.09 14:32 ·
// Иван Петров": документът остава главното в модала, а това, че е пипан и от
// кого, се вижда веднага. Разгъната показва записите от най-новия към
// създаването; най-новият запис с промени е отворен, другите — с едно щракване.
// Лентата и записите са бутони: Tab ги достига, Enter/Space ги отваря.
// Докато се зарежда — нищо (без прескачане на листа при бавна мрежа); BE без
// историята (404) — нищо; друга грешка — един приглушен ред.
import { useMemo, useState } from "react";
import { DownOutlined, HistoryOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { useProducts } from "../../../../hooks/useInventory";
import {
  useDocumentRevisions,
  type RevisionDocumentGroup,
} from "../../../../hooks/useInventoryRevisions";
import { useUnitDecimals } from "../qty";
import {
  buildRevisionEntries,
  formatRevisionWhen,
  revisionKindOf,
  type RevisionDiffContext,
} from "./revisionDiffModel";
import { RevisionEntryItem } from "./RevisionEntryItem";

type Props = {
  // списъкът, под който живее документът (ключът на заявката — виж хука)
  group: RevisionDocumentGroup;
  movementId: number;
};

export const DocumentRevisionsPanel = ({ group, movementId }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useViewport();
  const { data: products } = useProducts();
  const decimalsOf = useUnitDecimals();
  const { data, isError, error } = useDocumentRevisions(group, movementId);
  const [open, setOpen] = useState(false);

  const context = useMemo<RevisionDiffContext>(() => {
    const productById = new Map((products ?? []).map((product) => [product.id, product]));
    return {
      t: (key, options) => t(key, options),
      decimalsOf,
      unitOf: (productId) => productById.get(productId)?.unit,
      productNameOf: (productId) => productById.get(productId)?.name,
    };
  }, [products, decimalsOf, t]);
  const entries = useMemo(() => (data ? buildRevisionEntries(data, context) : []), [data, context]);

  if (isError) {
    // 404 = BE без историята (по-стара версия) — секцията просто я няма
    if ((error as { response?: { status?: number } } | null)?.response?.status === 404) return null;
    return (
      <div className="mb-3 text-xs leading-[normal] text-muted print:hidden">
        {t("inventoryPage.revLoadError")}
      </div>
    );
  }
  if (!data) return null;
  if (data.revisions.length === 0) {
    return (
      <div className="mb-3 flex items-center gap-1.5 text-xs leading-[normal] text-muted print:hidden">
        <HistoryOutlined aria-hidden className="text-[12px]" />
        {t("inventoryPage.revNone")}
      </div>
    );
  }

  const kind = revisionKindOf(data.movementType);
  const lastEdit = data.revisions[data.revisions.length - 1];
  const firstWithChanges = entries.find(
    (entry) => entry.type === "edit" && entry.changes.count > 0,
  );
  return (
    <section className="mb-4 overflow-hidden rounded-[10px] border border-shade-2 bg-panel print:hidden">
      <button
        type="button"
        aria-expanded={open}
        title={t(open ? "inventoryPage.revHideHistory" : "inventoryPage.revShowHistory")}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full cursor-pointer items-start gap-2 border-none bg-transparent px-3.5 py-2.5 text-left hover:bg-shade-1"
      >
        <HistoryOutlined aria-hidden className="mt-px text-[14px] text-brand-deep-ink" />
        <span className="min-w-0 flex-1 text-[13px] leading-[1.35]">
          <span className="font-semibold text-ink">{t("inventoryPage.revTitle")}</span>
          <span className="text-body">
            {" · "}
            {t("inventoryPage.revEdits", { count: data.revisions.length })}
            {" · "}
            {t("inventoryPage.revLast", {
              when: formatRevisionWhen(lastEdit.changedAt),
              name: lastEdit.changedByName || t("inventoryPage.revUnknownUser"),
            })}
          </span>
        </span>
        <DownOutlined
          aria-hidden
          className={`mt-1 text-[11px] text-muted transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <ol className="m-0 list-none border-t border-shade-2 p-0">
          {entries.map((entry) => (
            <RevisionEntryItem
              key={entry.id}
              entry={entry}
              kind={kind}
              defaultOpen={entry === firstWithChanges}
              isMobile={isMobile}
            />
          ))}
        </ol>
      )}
    </section>
  );
};

export default DocumentRevisionsPanel;
